import React, {
    useContext, useCallback, useState, useMemo
} from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ModalNextProvider,
    Button,
    Loader
} from '@jutro/components';
import { renderContentFromMetadata } from '@jutro/uiconfig';
import { IntlContext } from '@jutro/locale';
import { ViewModelServiceContext } from 'gw-portals-viewmodel-react';
import { valueTypeMap, getFormattedValue, processModalData } from './ScheduleItemsUtil';
import ScheduleItemModalPopover from './ScheduleItemModalPopover';
import messages from 'gw-components-platform-react/ScheduleItemsComponent/ScheduleItemsComponent.messages';
import styles from './ScheduleItemsComponent.module.scss';
import metadata from './ScheduleItemsComponent.metadata.json5';
import { TranslatorContext } from '@jutro/locale';
import modelMessages from './ScheduleItemsComponent.messages';
import {messages as commonMessages} from "../../nexus-platform-translations";

function ScheduleItemsComponent(props) {
    const viewModelService = useContext(ViewModelServiceContext);
    const intl = useContext(IntlContext);
    const [isLoading, setLoading] = useState(false);
    const {
        value: scheduleItem,
        labelPosition,
        id,
        readOnly,
        onScheduleChange,
        dataPath,
        path,
        showTitle
    } = props;
    const translator = useContext(TranslatorContext);

    const renderCell = useCallback((items, index, property) => {
        const { id: pathToValue, valueType } = property.data;

        if (valueType === 'ICON') {
            const { icon, onClick } = property.data;
            return (
                <Button
                    className="actionIcon"
                    icon={icon}
                    type="secondary"
                    onClick={onClick(items, index)}
                />
            );
        }

        const valueObject = _.get(items.itemData, pathToValue);
        const value = _.get(valueObject, valueTypeMap[valueType]);

        if (valueType === 'INTEGER' && _.has(property.data, 'currency')) {
            const { currency } = property.data;
            return intl.formatNumber(
                value,
                {
                    style: 'currency',
                    currency: currency,
                    currencyDisplay: 'code'
                }
            );
        }
        if (valueType === 'DATE') {
            return value ? intl.formatDate(new Date(value), { year: 'numeric', month: 'short', day: 'numeric' }) : '-';
        }
        if(valueType === 'BOOLEAN'){
            return value == 'true' ? 'Yes' : 'No';
        }
        return getFormattedValue(value, valueType, property.data);
    }, [intl]);

    const generateColumnOverrides = useCallback(
        (columnData) => {
            const overrideProps = columnData.map((info, index) => ({
                [`scheduleItemColumn${index}`]: {
                    onCell: renderCell,
                    header: info.label
                }
            }));
            return Object.assign({}, ...overrideProps);
        },
        [renderCell]
    );

    const showModal = useCallback(
        async (scheduleData) => {
            const componentProps = {
                propertyInfo: scheduleItem.propertyInfos,
                scheduleData: processModalData(scheduleData),
                viewModelService,
                scheduleItem,
                labelPosition,
                id,
                isNew: !scheduleData
            };

            const result = await ModalNextProvider.showModal(
                <ScheduleItemModalPopover {...componentProps} />
            );
            return result;
        },
        [id, labelPosition, scheduleItem, viewModelService]
    );

    const removeSchedule = useCallback(
        (scheduleToRemove) => () => {
            ModalNextProvider.showAlert({
                title: messages.scheduleRemoveTitle,
                message: messages.scheduleRemove,
                status: 'warning',
                icon: 'mi-error-outline',
                confirmButtonText: messages.delete,
                cancelButtonText: messages.cancel
            }).then(() => {
                const _path = path || dataPath;
                const clonedSchedule = _.cloneDeep(scheduleItem);
                clonedSchedule.scheduleItems = clonedSchedule.scheduleItems.filter(
                    (schedule) => !_.isEqual(schedule, scheduleToRemove)
                );

                if (onScheduleChange) {
                    setLoading(true);
                    onScheduleChange(clonedSchedule, _path).finally(() => setLoading(false));
                }
            }, _.noop);
        },
        [onScheduleChange, path, scheduleItem, dataPath]
    );

    const editSchedule = useCallback(
        (schedule, index) => () => {
            const localPathToSchedule = `scheduleItems[${index}]`;

            showModal(schedule)
                .then((updatedSchedule) => {
                    const _path = path || dataPath;
                    if (scheduleItem.patternName === 'HOP_PersonalItemsSchItems_EY' && !isValidPersonalItemsSchItems(updatedSchedule, scheduleItem)) {
                        return;
                    }

                    const clonedSchedule = _.cloneDeep(scheduleItem);
                    _.set(clonedSchedule, localPathToSchedule, updatedSchedule);

                    if (onScheduleChange) {
                        setLoading(true);
                        onScheduleChange(clonedSchedule, _path).finally(() => setLoading(false));
                    }
                })
                .catch(_.noop);
        },
        [onScheduleChange, path, scheduleItem, showModal, dataPath]
    );

    const addSchedule = useCallback(() => {
        showModal()
            .then((schedule) => {
                const _path = path || dataPath;
                if (scheduleItem.patternName === 'HOP_PersonalItemsSchItems_EY' && !isValidPersonalItemsSchItems(schedule, scheduleItem)) {
                    return;
                }

                const clonedSchedule = _.cloneDeep(scheduleItem);
                const newScheduleItem = {
                    '@deserialization-class': scheduleItem.deserializationClass,
                    ...schedule
                };

                clonedSchedule.scheduleItems = [...scheduleItem.scheduleItems, newScheduleItem];

                if (onScheduleChange) {
                    setLoading(true);
                    onScheduleChange(clonedSchedule, _path).finally(() => setLoading(false));
                }
            })
            .catch(_.noop);
    }, [onScheduleChange, path, scheduleItem, showModal, dataPath]);

    const columnData = useMemo(() => {
        const orderedPropertyInfo = _.sortBy(scheduleItem.propertyInfos, 'order');
        const icons = [
            {
                valueType: 'ICON',
                icon: 'mi-delete',
                onClick: removeSchedule
            },
            {
                valueType: 'ICON',
                icon: 'mi-edit',
                onClick: editSchedule
            }
        ];
        const iconsShow = !readOnly ? icons : [];
        return [...orderedPropertyInfo, ...iconsShow];
    }, [editSchedule, readOnly, removeSchedule, scheduleItem.propertyInfos]);

    const renderAdd = useCallback(
        () => (
            <Button icon="mi-add" onClick={addSchedule} type="secondary">
                {messages.scheduleAdd}
            </Button>
        ),
        [addSchedule]
    );

    /*
      Items and its allowed item types
     */
    const getPersonalArtTypelistApplicableValues = () => {
        const map = new Map();
        //itemType: item
        map.set('ItemTypeNonPro_EY', ['HOP_PersonalItemsSchItemsFineArts_EY', 'HOP_PersonalItemsSchItemsCameraEquip_EY', 'HOP_PersonalItemsSchItemsMusicalInstr_EY']);
        map.set('HOP_ItemTypeProfeComm_EY', ['HOP_PersonalItemsSchItemsFineArts_EY', 'HOP_PersonalItemsSchItemsCameraEquip_EY', 'HOP_PersonalItemsSchItemsMusicalInstr_EY', 'HOP_PersonalItemsSchItemsTools_EY']);
        map.set('HOP_ItemTypeHobbyist_EY', ['HOP_PersonalItemsSchItemsTools_EY']);
        map.set('HOP_ItemTypeSoftware_EY', ['HOP_PersonalItemsSchItemsPsnlComputer_EY']);
        map.set('HOP_ItemTypeDsktpAndAccessories_EY', ['HOP_PersonalItemsSchItemsPsnlComputer_EY']);
        return map;
    }

    const findApplicableItemTypesForItem = (itemCode) => {
        let itemTypeCodes = [];
        for (let [itemTypeCodeKey, itemCodeValues] of  getPersonalArtTypelistApplicableValues().entries()) {
            if(itemCodeValues.includes(itemCode)) {
                itemTypeCodes.push(itemTypeCodeKey);
            }
        }
        return itemTypeCodes;
    }

    const showErrorMsg = (scheduleItem, itemCode, itemTypeCode, messageId, messageArgs) => {
        const itemTypePropInfo = scheduleItem.propertyInfos.find(element => element.id === 'PersItemSchItemType_EY');
        const itemName = getDisplayKeyForCode(itemCode, scheduleItem.propertyInfos.find(element => element.id === 'HOP_PersItemsItem_EY').availableTypeListValues);
        const itemTypeName = getDisplayKeyForCode(itemTypeCode, itemTypePropInfo.availableTypeListValues);
        const message = translator(messageId, { ...messageArgs, itemTypeName: itemTypeName, itemName: itemName });
        showErrorPopup(message);
    }

    const showErrorPopup = (message) => {
        ModalNextProvider.showAlert({
            title: commonMessages.genericError,
            message: message,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok
        }).catch(_.noop);
    }


    const isValidPersonalItemsSchItems = (item, scheduleItem) => {
        let itemCode = item.itemData.HOP_PersItemsItem_EY.typeCodeValue;
        let itemTypeCode = item.itemData.PersItemSchItemType_EY.typeCodeValue;

        const applicableItemTypeCodes = findApplicableItemTypesForItem(itemCode);

        if (applicableItemTypeCodes.length > 0 && !applicableItemTypeCodes.includes(itemTypeCode)) {
            const itemTypePropInfo = scheduleItem.propertyInfos.find(element => element.id === 'PersItemSchItemType_EY');
            let itemTypesMsg;
            applicableItemTypeCodes.forEach(itemTypeCodeEle => {
                if(!itemTypesMsg) {
                    itemTypesMsg = getDisplayKeyForCode(itemTypeCodeEle, itemTypePropInfo.availableTypeListValues);
                } else {
                    itemTypesMsg = itemTypesMsg + ' (or) '+ getDisplayKeyForCode(itemTypeCodeEle, itemTypePropInfo.availableTypeListValues);
                }
            });
            showErrorMsg(scheduleItem, itemCode, itemTypeCode, modelMessages.itemTypeNotApplicableForItem, { listOfItemTypes: itemTypesMsg });
            return false;
        } else if (applicableItemTypeCodes.length == 0 && itemTypeCode !== 'HOP_ItemTypeNone_EY' && itemTypeCode !== 'None') {
            showErrorMsg(scheduleItem, itemCode, itemTypeCode, modelMessages.selectNone, null);
            return false;
        } else {
            let isAppraisalReceived = item.itemData.PersItemsApraslRecivd_EY ? item.itemData.PersItemsApraslRecivd_EY.booleanValue : false;
            if (isAppraisalReceived) {
                let appraisedDate = item.itemData.PersItemsApraslDate_EY ? item.itemData.PersItemsApraslDate_EY.dateValue : undefined;
                let propLabel = scheduleItem.propertyInfos.find(element => element.id === 'PersItemsApraslDate_EY').label;
                if (appraisedDate === undefined || appraisedDate == null) {
                    const message = translator(modelMessages.fillField, { fieldName: propLabel });
                    showErrorPopup(message);
                    return false;
                }
            }
        }
        return true;
    }

    const getDisplayKeyForCode = (code, list) => {
        for (let i = 0; i < list.length; i++) {
            if (list[i].code === code) {
                return list[i].displayKey;
            }
        }
    }

    const overrideProps = useMemo(() => {
        return {
            scheduleItemTable: {
                data: columnData,
                title: showTitle ? scheduleItem.displayName : undefined,
                titleAction: !readOnly ? renderAdd : undefined,
                placeholder: readOnly ? messages.noItems : ''
            },
            ...generateColumnOverrides(columnData)
        };
    }, [
        columnData,
        scheduleItem.displayName,
        showTitle,
        readOnly,
        renderAdd,
        generateColumnOverrides
    ]);

    const readValue = useCallback(
        (elementId, schedulePath) => {
            return _.get(scheduleItem, schedulePath);
        },
        [scheduleItem]
    );

    const resolvers = {
        resolveValue: readValue,
        resolveClassNameMap: styles
    };

    if (isLoading) {
        return <Loader loaded={!isLoading} />;
    }

    return renderContentFromMetadata(metadata.componentContent, overrideProps, resolvers);
}

ScheduleItemsComponent.propTypes = {
    value: PropTypes.shape({
        scheduleItems: PropTypes.arrayOf(PropTypes.shape({})),
        propertyInfos: PropTypes.arrayOf(PropTypes.shape({}))
    }).isRequired,
    onScheduleChange: PropTypes.func,
    path: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    labelPosition: PropTypes.string,
    readOnly: PropTypes.bool,
    showTitle: PropTypes.bool
};

ScheduleItemsComponent.defaultProps = {
    onScheduleChange: undefined,
    readOnly: false,
    labelPosition: undefined,
    showTitle: true
};

export default ScheduleItemsComponent;
